import React, { useMemo, useState } from "react";
import {
  FiEdit2,
  FiPlus,
  FiCheck,
  FiX,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiArrowRight,
  FiAlertTriangle,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import styles from "./CabinesSection.module.scss";
import Axios from "_services/caller.service";

const getCabTypeTagClass = (cabtype, styles) => {
  switch ((cabtype || "").toUpperCase()) {
    case "CONSULTING":    return styles.cabTypeTagConsulting;
    case "RECEPTION":     return styles.cabTypeTagReception;
    case "DEMONSTRATION": return styles.cabTypeTagDemonstration;
    default:              return styles.cabTypeTag;
  }
};

function CabinesSection({ sub, user, onMoved }) {
  const { t } = useTranslation();
  const [cabSearch, setCabSearch] = useState("");
  const [cabinesLocal, setCabinesLocal] = useState(sub?.cabines || []);
  const [editingCabId, setEditingCabId] = useState(null);
  const [editingCabName, setEditingCabName] = useState("");
  const [editingCabType, setEditingCabType] = useState("CONSULTING");
  const [isAddingCab, setIsAddingCab] = useState(false);
  const [newCabName, setNewCabName] = useState("");
  const [newCabType, setNewCabType] = useState("CONSULTING");
  const [deletingCabId, setDeletingCabId] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletePasswordConfirm, setDeletePasswordConfirm] = useState("");
  const [deletePasswordError, setDeletePasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [showDeletePasswordConfirm, setShowDeletePasswordConfirm] = useState(false);

  // ----- Move cabine state -----
  const [movingCab, setMovingCab] = useState(null); // { id, cabname }
  const [moveTargetSubId, setMoveTargetSubId] = useState("");
  const [moveError, setMoveError] = useState("");

  // Liste des autres abonnements du même owner (cible potentielle)
  const otherSubs = useMemo(() => {
    const all = Array.isArray(user?.subscriptions) ? user.subscriptions : [];
    return all.filter((s) => s.id !== sub?.id);
  }, [user, sub]);

  // Synchroniser avec le prop sub quand il change
  React.useEffect(() => {
    setCabinesLocal(sub?.cabines || []);
  }, [sub?.id]); // Dépendre de sub.id pour éviter les boucles infinies

  const cabines = useMemo(() => {
    const all = cabinesLocal;
    const q = cabSearch.toLowerCase().trim();
    if (!q) return all;

    return all.filter((c) => {
      const name = (c.cabname || "").toLowerCase();
      const type = (c.cabtype || "").toLowerCase();
      const id = String(c.id || "").toLowerCase();
      return name.includes(q) || type.includes(q) || id.includes(q);
    });
  }, [cabinesLocal, cabSearch]);

  const handleEditCabName = (cab) => {
    setEditingCabId(cab.id);
    setEditingCabName(cab.cabname || "");
    setEditingCabType(cab.cabtype || "CONSULTING");
  };

  const handleSaveEditCabName = async (cabId) => {
    if (!editingCabName.trim()) return;

    setLoading(true);
    try {
      await Axios.put(`/subscriptions/${sub.id}/cabines/${cabId}`, {
        cabname: editingCabName.trim(),
        cabtype: editingCabType,
      });

      // Mettre à jour le state local avec une nouvelle référence
      setCabinesLocal((prevCabines) =>
        prevCabines.map((c) =>
          c.id === cabId ? { ...c, cabname: editingCabName.trim(), cabtype: editingCabType } : c
        )
      );

      toast.success(t("cabines_section.cabine_updated"));
      setEditingCabId(null);
      setEditingCabName("");
      setEditingCabType("CONSULTING");
    } catch (err) {
      console.error("❌ Erreur modification cabine:", err);
      const errorMsg = err.response?.data?.error || t("cabines_section.cabine_update_error");
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditCabName = () => {
    setEditingCabId(null);
    setEditingCabName("");
    setEditingCabType("CONSULTING");
  };

  const handleAddCabine = async () => {
    if (!newCabName.trim()) return;

    setLoading(true);
    try {
      const res = await Axios.post(`/subscriptions/${sub.id}/cabines`, {
        cabname: newCabName.trim(),
        cabtype: newCabType,
        userId: user?.id,
      });

      // Récupérer l'ID de la cabine créée
      const cabId = res.data?.data?.id || res.data?.id;
      
      // Ajouter la nouvelle cabine au state local avec une nouvelle référence
      const newCab = {
        id: cabId,
        cabname: newCabName.trim(),
        cabtype: newCabType,
        subscription_id: sub.id,
        features: "Dirac,AvoidAdminCheck",
        machine: "",
      };
      
      setCabinesLocal((prevCabines) => [...prevCabines, newCab]);

      toast.success(t("cabines_section.cabine_created"));
      setNewCabName("");
      setNewCabType("CONSULTING");
      setIsAddingCab(false);
    } catch (err) {
      console.error("❌ Erreur ajout cabine:", err);
      const errorMsg = err.response?.data?.error || t("cabines_section.cabine_create_error");
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCabine = (cabId) => {
    setDeletingCabId(cabId);
    setDeletePassword("");
    setDeletePasswordConfirm("");
    setDeletePasswordError("");
  };

  const handleConfirmDelete = async () => {
    // Vérifier que les deux mots de passe sont remplis
    if (!deletePassword.trim() || !deletePasswordConfirm.trim()) {
      const errorMsg = t("cabines_section.enter_password_twice");
      setDeletePasswordError(errorMsg);
      return;
    }

    // Vérifier que les deux mots de passe correspondent
    if (deletePassword !== deletePasswordConfirm) {
      const errorMsg = t("cabines_section.passwords_mismatch");
      setDeletePasswordError(errorMsg);
      return;
    }

    setLoading(true);
    setDeletePasswordError("");

    try {
      await Axios.delete(`/subscriptions/${sub.id}/cabines/${deletingCabId}`, {
        data: { adminPassword: deletePassword },
      });

      // Supprimer la cabine du state local avec une nouvelle référence
      setCabinesLocal((prevCabines) =>
        prevCabines.filter((c) => c.id !== deletingCabId)
      );

      toast.success(t("cabines_section.cabine_deleted"));

      // Réinitialiser
      setDeletingCabId(null);
      setDeletePassword("");
      setDeletePasswordConfirm("");
      setDeletePasswordError("");
    } catch (err) {
      console.error("❌ Erreur suppression cabine:", err);
      
      // Vérifier si c'est une erreur 401 (non-authentifié)
      if (err.response?.status === 401) {
        const errorMsg = err.response?.data?.error || t("cabines_section.auth_error");
        setDeletePasswordError(errorMsg);
        toast.error(errorMsg);
        return;
      }
      
      const errorMsg = err.response?.data?.error || t("cabines_section.delete_error");
      setDeletePasswordError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeletingCabId(null);
    setDeletePassword("");
    setDeletePasswordConfirm("");
    setDeletePasswordError("");
  };

  // ----- Move cabine handlers -----
  const handleOpenMove = (cab) => {
    setMovingCab({ id: cab.id, cabname: cab.cabname });
    setMoveTargetSubId("");
    setMoveError("");
  };

  const handleCloseMove = () => {
    if (loading) return;
    setMovingCab(null);
    setMoveTargetSubId("");
    setMoveError("");
  };

  const handleConfirmMove = async () => {
    if (!movingCab || !moveTargetSubId) {
      setMoveError(t("cabines_section.move_select_target"));
      return;
    }
    setLoading(true);
    setMoveError("");
    try {
      await Axios.patch(
        `/subscriptions/${sub.id}/cabines/${movingCab.id}/move`,
        { toSubId: Number(moveTargetSubId) }
      );

      // Optimistic local update : retire la cabine de la liste affichée
      setCabinesLocal((prev) => prev.filter((c) => c.id !== movingCab.id));

      const targetSub = otherSubs.find((s) => s.id === Number(moveTargetSubId));
      toast.success(
        t("cabines_section.cabine_moved", {
          name: movingCab.cabname,
          target: targetSub?.name || `#${moveTargetSubId}`,
        })
      );

      setMovingCab(null);
      setMoveTargetSubId("");

      // Demande au parent de recharger les données pour rafraîchir tous les abos
      if (typeof onMoved === "function") {
        onMoved();
      }
    } catch (err) {
      console.error("❌ Erreur déplacement cabine:", err);
      const msg = err.response?.data?.error || t("cabines_section.move_error");
      setMoveError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.cabinesSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerTop}>
          <div className={styles.sectionTitle}>{t("cabines_section.title")}</div>
          <button
            type="button"
            className={styles.btnAddCabHeader}
            onClick={() => setIsAddingCab(true)}
          >
            <FiPlus /> {t("cabines_section.add_cabine")}
          </button>
        </div>
        {isAddingCab && (
          <div className={styles.addCabineForm}>
            <input
              type="text"
              className={styles.inputCabName}
              placeholder={t("cabines_section.cabine_name_placeholder")}
              value={newCabName}
              onChange={(e) => setNewCabName(e.target.value)}
            />
            <select
              className={styles.selectCabType}
              value={newCabType}
              onChange={(e) => setNewCabType(e.target.value)}
            >
              <option value="CONSULTING">CONSULTING</option>
              <option value="RECEPTION">RECEPTION</option>
              <option value="DEMONSTRATION">DEMONSTRATION</option>
              <option value="LAPTOP_PRO">LAPTOP_PRO</option>
              <option value="LAPTOP_RESTRICTED">LAPTOP_RESTRICTED</option>
            </select>
            <div className={styles.formButtonGroup}>
              <button
                type="button"
                className={styles.btnCancelAdd}
                onClick={() => {
                  setIsAddingCab(false);
                  setNewCabName("");
                  setNewCabType("CONSULTING");
                }}
              >
                {t("cabines_section.cancel")}
              </button>
               <button
                type="button"
                className={styles.btnConfirmAdd}
                onClick={handleAddCabine}
                disabled={!newCabName.trim() || loading}
              >
                {t("cabines_section.add")}
              </button>
            </div>
          </div>
        )}
        <input
          className={styles.searchInput}
          type="text"
          placeholder={t("cabines_section.search_cabine")}
          value={cabSearch}
          onChange={(e) => setCabSearch(e.target.value)}
        />
      </div>

      {cabines.length === 0 && !isAddingCab ? (
        <div className={styles.empty}>{t("cabines_section.no_cabine")}</div>
      ) : (
        <div className={styles.cabList}>
          {cabines.map((cab) => {
            const isEditing = editingCabId === cab.id;

            return (
              <div key={cab.id} className={styles.cabItem}>
                <div className={styles.cabLine}>
                  <div className={styles.cabLeft}>
                    {isEditing ? (
                      <div className={styles.editNameContainer}>
                        <input
                          type="text"
                          className={styles.editNameInput}
                          value={editingCabName}
                          onChange={(e) => setEditingCabName(e.target.value)}
                        />
                        <select
                          className={styles.selectCabType}
                          value={editingCabType}
                          onChange={(e) => setEditingCabType(e.target.value)}
                        >
                          <option value="CONSULTING">CONSULTING</option>
                          <option value="RECEPTION">RECEPTION</option>
                          <option value="DEMONSTRATION">DEMONSTRATION</option>
                          <option value="LAPTOP_PRO">LAPTOP_PRO</option>
                          <option value="LAPTOP_RESTRICTED">LAPTOP_RESTRICTED</option>
                        </select>
                        <div className={styles.editButtonGroup}>
                          <button
                            type="button"
                            className={styles.btnCancelEdit}
                            onClick={handleCancelEditCabName}
                            disabled={loading}
                          >
                            {t("cabines_section.cancel")}
                          </button>
                          <button
                            type="button"
                            className={styles.btnSaveEdit}
                            onClick={() => handleSaveEditCabName(cab.id)}
                            disabled={!editingCabName.trim() || loading}
                          >
                            {t("cabines_section.save")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.cabInfo}>
                        <span className={styles.cabName}>
                          {cab.cabname || t("cabines_section.cabine_default")}
                        </span>
                        <span className={getCabTypeTagClass(cab.cabtype, styles)}>
                          {cab.cabtype || "-"}
                        </span>
                        <span className={styles.cabIdInfo}>
                          Cab: {cab.id} | Sub: {sub.id} 
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={styles.cabRight}>
                    {!isEditing && (
                      <button
                        type="button"
                        className={styles.btnIconEdit}
                        onClick={() => handleEditCabName(cab)}
                        title={t("cabines_section.edit_name_title")}
                        aria-label={t("cabines_section.edit")}
                      >
                        <FiEdit2 />
                      </button>
                    )}
                    {!isEditing && otherSubs.length > 0 && (
                      <button
                        type="button"
                        className={styles.btnIconMove}
                        onClick={() => handleOpenMove(cab)}
                        title={t("cabines_section.move_cabine_title")}
                        aria-label={t("cabines_section.move")}
                      >
                        <FiArrowRight />
                      </button>
                    )}
                    {!isEditing && (
                      <button
                        type="button"
                        className={styles.btnIconDelete}
                        onClick={() => handleDeleteCabine(cab.id)}
                        title={t("cabines_section.delete_cabine_title")}
                        aria-label={t("cabines_section.delete")}
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                </div>

                {deletingCabId === cab.id && (
                  <div className={styles.deleteConfirmForm}>
                    <div className={styles.deleteWarning}>
                      ⚠️ {t("cabines_section.delete_warning")}
                    </div>
                    <div className={styles.passwordInputWrapper}>
                      <input
                        type={showDeletePassword ? "text" : "password"}
                        className={styles.inputDeletePassword}
                        placeholder={t("cabines_section.admin_password")}
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                      />
                      <button
                        type="button"
                        className={styles.togglePasswordBtn}
                        onClick={() => setShowDeletePassword(!showDeletePassword)}
                        title={showDeletePassword ? t("cabines_section.hide") : t("cabines_section.show")}
                      >
                        {showDeletePassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    <div className={styles.passwordInputWrapper}>
                      <input
                        type={showDeletePasswordConfirm ? "text" : "password"}
                        className={styles.inputDeletePassword}
                        placeholder={t("cabines_section.confirm_password")}
                        value={deletePasswordConfirm}
                        onChange={(e) => setDeletePasswordConfirm(e.target.value)}
                      />
                      <button
                        type="button"
                        className={styles.togglePasswordBtn}
                        onClick={() => setShowDeletePasswordConfirm(!showDeletePasswordConfirm)}
                        title={showDeletePasswordConfirm ? t("cabines_section.hide") : t("cabines_section.show")}
                      >
                        {showDeletePasswordConfirm ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    {deletePasswordError && (
                      <div className={styles.deleteError}>{deletePasswordError}</div>
                    )}
                    <div className={styles.deleteButtonGroup}>
                      <button
                        type="button"
                        className={styles.btnDeleteConfirm}
                        onClick={handleConfirmDelete}
                        disabled={!deletePassword.trim() || !deletePasswordConfirm.trim() || loading}
                      >
                        <FiTrash2 /> {t("cabines_section.delete")}
                      </button>
                      <button
                        type="button"
                        className={styles.btnDeleteCancel}
                        onClick={handleCancelDelete}
                        disabled={loading}
                      >
                        <FiX /> {t("cabines_section.cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modale de confirmation : déplacer une cabine vers un autre abonnement */}
      {movingCab && (
        <div
          className={styles.moveModalOverlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) handleCloseMove();
          }}
        >
          <div className={styles.moveModalContent}>
            <h3 className={styles.moveModalTitle}>
              <FiArrowRight /> {t("cabines_section.move_cabine_title")}
            </h3>

            <div className={styles.moveModalWarning}>
              <FiAlertTriangle />
              <span>
                {t("cabines_section.move_warning", { name: movingCab.cabname })}
              </span>
            </div>

            <label className={styles.moveModalLabel}>
              {t("cabines_section.move_target_label")}
            </label>
            <select
              className={styles.moveModalSelect}
              value={moveTargetSubId}
              onChange={(e) => setMoveTargetSubId(e.target.value)}
              disabled={loading}
            >
              <option value="">{t("cabines_section.move_select_placeholder")}</option>
              {otherSubs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || `Sub #${s.id}`}
                  {s.subid ? ` — Odoo ${s.subid}` : ""}
                </option>
              ))}
            </select>

            {moveError && <div className={styles.moveModalError}>{moveError}</div>}

            <div className={styles.moveModalButtons}>
              <button
                type="button"
                className={styles.btnMoveCancel}
                onClick={handleCloseMove}
                disabled={loading}
              >
                <FiX /> {t("common.cancel")}
              </button>
              <button
                type="button"
                className={styles.btnMoveConfirm}
                onClick={handleConfirmMove}
                disabled={!moveTargetSubId || loading}
              >
                <FiCheck /> {t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CabinesSection;
